/**
 * Form submission hook for EntityForm.
 *
 * Manages the full submission lifecycle: validation, payload transformation,
 * API mutation, and success/error handling. Uses `useMutation` from
 * `@tanstack/react-query` for the actual API call.
 *
 * @see Spec section 4.2 for the hook signature.
 * @see Spec section 7.1 for the submission workflow.
 * @see Plan section 3.5 for implementation details.
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';

import type {
  EntityFormConfig,
  FieldConfig,
  UseEntityFormReturn,
} from '../types';
import { buildPayload } from '../workflow/payloadBuilder';
import { handleSubmitSuccess, handleSubmitError } from '../workflow/handlers';
import type { SubmitErrorResult } from '../workflow/handlers';
import { workflowRegistry } from '../workflow/registry';

// ---------------------------------------------------------------------------
// Options
// ---------------------------------------------------------------------------

/**
 * Options accepted by `useFormSubmission`.
 *
 * The calling hook (`useEntityForm`) supplies form state references so that
 * the submission flow can validate, build payloads, set errors, and invoke
 * lifecycle handlers without circular dependencies.
 */
export interface UseFormSubmissionOptions {
  /** The full EntityForm configuration. */
  config: EntityFormConfig;

  /** Current form values keyed by field name. */
  values: Record<string, unknown>;

  /** Current field visibility map (true = visible). */
  visibilityMap: Record<string, boolean>;

  /** Validates all visible fields. Returns `true` when the form is valid. */
  validateForm: () => Promise<boolean>;

  /** Current field-level errors keyed by field name. */
  errors: Record<string, string | undefined>;

  /** Sets an error on a specific field. */
  setError: (name: string, message: string) => void;

  /** Called when the API returns a successful response. */
  onSuccess?: (response: unknown) => void;

  /** Called when the API returns an error. */
  onError?: (error: unknown) => void;

  /**
   * Custom mutation function that performs the API call.
   *
   * Receives the built payload and the endpoint configuration, and must
   * return a promise that resolves with the API response. When omitted a
   * default `fetch`-based implementation is used.
   */
  mutationFn?: (
    payload: Record<string, unknown> | FormData,
    config: EntityFormConfig,
  ) => Promise<unknown>;
}

// ---------------------------------------------------------------------------
// Return Type
// ---------------------------------------------------------------------------

/**
 * Public return type of `useFormSubmission`.
 */
export interface UseFormSubmissionReturn {
  /** Trigger the full submit flow (validate -> transform -> mutate -> handle result). */
  submit: () => Promise<void>;

  /** Whether a mutation is currently in flight. */
  isSubmitting: boolean;

  /** Whether the most recent submission completed successfully. */
  isSubmitSuccessful: boolean;

  /** How many times `submit()` has been called (regardless of outcome). */
  submitCount: number;

  /** Form-level error message (e.g. from a failed API call). */
  formError: string | null;

  /** Manually set or clear the form-level error. */
  setFormError: (error: string | null) => void;
}

// ---------------------------------------------------------------------------
// Focus helper
// ---------------------------------------------------------------------------

/**
 * Moves focus to the first field that has a validation error.
 *
 * Fields are expected to render with `id="field-{name}"` so that this
 * function can locate them in the DOM.
 *
 * @param errors - Current field errors keyed by field name.
 */
function focusFirstError(errors: Record<string, string | undefined>): void {
  const firstErrorField = Object.keys(errors).find((key) => errors[key]);
  if (firstErrorField) {
    document.getElementById(`field-${firstErrorField}`)?.focus();
  }
}

// ---------------------------------------------------------------------------
// Submission error wrapper
// ---------------------------------------------------------------------------

/**
 * Custom error class that wraps structured API error data.
 *
 * Thrown by the default mutation function so that `handleSubmitError` can
 * extract field-level errors and a human-readable message from the response.
 */
class SubmissionError extends Error {
  /** The structured error payload from the API. */
  readonly data: unknown;

  /** The HTTP status code. */
  readonly status: number;

  constructor(status: number, statusText: string, data: unknown) {
    super(statusText);
    this.name = 'SubmissionError';
    this.status = status;
    this.data = data;
  }
}

// ---------------------------------------------------------------------------
// Default mutation function
// ---------------------------------------------------------------------------

/**
 * Default fetch-based mutation function.
 *
 * Calls `fetch()` with the configured endpoint, method, and payload.
 * Throws a `SubmissionError` on non-2xx responses so that `useMutation`
 * treats them as errors.
 *
 * @param payload - The request body (plain object or FormData).
 * @param config  - The full EntityForm configuration (used for endpoint/method).
 * @returns The parsed JSON response.
 */
async function defaultMutationFn(
  payload: Record<string, unknown> | FormData,
  config: EntityFormConfig,
): Promise<unknown> {
  const isFormData = payload instanceof FormData;

  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (!isFormData) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(config.endpoint.erpEndpoint, {
    method: config.endpoint.method,
    headers,
    body: isFormData ? payload : JSON.stringify(payload),
  });

  if (!response.ok) {
    // Attempt to parse the error body for downstream handlers
    let errorBody: unknown;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = { message: response.statusText };
    }

    throw new SubmissionError(response.status, response.statusText, errorBody);
  }

  return response.json() as Promise<unknown>;
}

// ---------------------------------------------------------------------------
// Internal: form actions shim
// ---------------------------------------------------------------------------

/**
 * Builds a minimal shim of `UseEntityFormReturn` from the options available
 * to the submission hook.
 *
 * `handleSubmitSuccess` and `handleSubmitError` accept a full
 * `UseEntityFormReturn` so they can reset the form, set field errors, etc.
 * At this layer we only have a subset of the full form API. The shim
 * satisfies the interface with no-op stubs for methods that are wired at
 * the orchestrator level (`useEntityForm`).
 *
 * This keeps the submission hook self-contained while still allowing the
 * workflow handlers to operate.
 */
function buildFormActionsShim(
  options: UseFormSubmissionOptions,
): UseEntityFormReturn {
  const { config, values, errors, setError } = options;

  // Construct a minimal object that satisfies the UseEntityFormReturn shape.
  // Only the methods actually called by handlers need real implementations;
  // the rest are no-op stubs.
  const noop = () => {};
  const noopPromise = () => Promise.resolve(undefined);

  const resolvedFields: Record<string, FieldConfig & { visible: boolean; required: boolean }> = {};

  return {
    values,
    initialValues: {},
    getValue: (name: string) => values[name],
    setValue: noop as (name: string, value: unknown) => void,
    setValues: noop as (values: Record<string, unknown>) => void,
    getError: (name: string) => errors[name],
    setError,
    clearError: noop as (name: string) => void,
    isTouched: () => false,
    setTouched: noop as (name: string) => void,
    isFieldDirty: () => false,
    getFieldProps: (name: string) => ({
      value: values[name],
      onChange: noop as (value: unknown) => void,
      onBlur: noop,
      error: !!errors[name],
      errorMessage: errors[name],
      disabled: false,
      required: false,
      name,
      id: `field-${name}`,
    }),
    errors,
    touched: {},
    validateField: noopPromise as (name: string) => Promise<string | undefined>,
    validateForm: options.validateForm,
    isDirty: false,
    isValid: true,
    isSubmitting: false,
    isSubmitSuccessful: false,
    submitCount: 0,
    formError: null,
    submit: () => Promise.resolve(),
    reset: noop as (newInitialValues?: Record<string, unknown>) => void,
    cancel: noop,
    clearErrors: noop,
    isFieldVisible: () => true,
    isFieldRequired: () => false,
    isLoading: false,
    fetchError: null,
    config,
    resolvedFields,
  };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Manages the EntityForm submission lifecycle.
 *
 * The hook orchestrates the following flow on each `submit()` call:
 *
 * 1. **Validate** -- calls `validateForm()`. If validation fails, focuses the
 *    first error field and returns early.
 * 2. **Transform** -- if `workflow.onBeforeSubmit.transform` is configured,
 *    looks up the transform in the workflow registry and applies it.
 * 3. **Build payload** -- calls `buildPayload()` to filter hidden fields,
 *    apply request mappings, and convert to FormData when needed.
 * 4. **Mutate** -- executes the API call via `useMutation`.
 * 5. **Handle result** -- on success, runs `handleSubmitSuccess()` and calls
 *    the optional `onSuccess` callback. On error, runs `handleSubmitError()`,
 *    applies field errors, and calls the optional `onError` callback.
 *
 * @param options - Configuration and form state references.
 * @returns Submission state and the `submit` action.
 *
 * @example
 * ```ts
 * const submission = useFormSubmission({
 *   config,
 *   values,
 *   visibilityMap,
 *   validateForm,
 *   errors,
 *   setError,
 *   onSuccess: (res) => navigate('/list'),
 * });
 *
 * // Later, in a submit handler:
 * await submission.submit();
 * ```
 */
export function useFormSubmission(
  options: UseFormSubmissionOptions,
): UseFormSubmissionReturn {
  const {
    config,
    validateForm,
    mutationFn,
  } = options;

  // ---- Local state ----

  const [submitCount, setSubmitCount] = useState(0);
  const [isSubmitSuccessful, setIsSubmitSuccessful] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // ---- Refs for latest values (avoid stale closures in mutation callbacks) ----

  const optionsRef = useRef(options);

  useEffect(() => {
    optionsRef.current = options;
  });

  // ---- Mutation ----

  const executeMutation = mutationFn ?? defaultMutationFn;

  const mutation = useMutation<unknown, unknown, Record<string, unknown> | FormData>({
    mutationFn: (payload: Record<string, unknown> | FormData) =>
      executeMutation(payload, config),
  });

  // `useMutation` returns a NEW result object on every render, but its
  // `mutateAsync` function is referentially stable. Capture it so the `submit`
  // callback can depend on the stable function instead of the churning result
  // object — otherwise `submit` (and the memoised form API that lists it)
  // would get a fresh identity on every render.
  const { mutateAsync } = mutation;

  // ---- Submit flow ----

  const submit = useCallback(async (): Promise<void> => {
    const currentOptions = optionsRef.current;

    // Increment submit count on every attempt
    setSubmitCount((prev) => prev + 1);

    // Clear previous form-level error
    setFormError(null);
    setIsSubmitSuccessful(false);

    // 1. Validate
    const isValid = await validateForm();
    if (!isValid) {
      focusFirstError(currentOptions.errors);
      return;
    }

    // 2. Build payload
    let payload: Record<string, unknown> | FormData = buildPayload(
      currentOptions.values,
      config,
      currentOptions.visibilityMap,
    );

    // 3. Run onBeforeSubmit transform (if configured)
    const transformKey = config.workflow?.onBeforeSubmit?.transform;
    if (transformKey) {
      const transform = workflowRegistry.getTransform(transformKey);
      if (transform && !(payload instanceof FormData)) {
        payload = transform(payload);
      }
    }

    // 4. Execute mutation
    try {
      const response = await mutateAsync(payload);

      // 5a. Success
      setIsSubmitSuccessful(true);

      // Run the configured success handlers.
      // `handleSubmitSuccess` expects (response, config, formActions). We build
      // a minimal formActions shim that exposes only the pieces the handler uses
      // (reset and setError). The full UseEntityFormReturn is available when
      // useEntityForm composes everything together. At this layer we only have
      // a subset, so we pass what we can.
      handleSubmitSuccess(response, config, buildFormActionsShim(currentOptions));

      currentOptions.onSuccess?.(response);
    } catch (error: unknown) {
      // 5b. Error
      setIsSubmitSuccessful(false);

      const errorResult: SubmitErrorResult = handleSubmitError(
        error,
        config,
        buildFormActionsShim(currentOptions),
      );

      // Apply form-level error
      setFormError(errorResult.formError);

      // Apply field-level errors
      for (const [fieldName, message] of Object.entries(errorResult.fieldErrors)) {
        currentOptions.setError(fieldName, message);
      }

      currentOptions.onError?.(error);
    }
  }, [config, mutateAsync, validateForm]);

  // ---- Return ----
  // Memoised so the whole return object is referentially stable and only
  // changes when an underlying member changes. `useEntityForm` composes this
  // object into its own memoised public API, so an unstable literal would
  // defeat that memoisation.

  const isSubmitting = mutation.isPending;

  return useMemo<UseFormSubmissionReturn>(
    () => ({
      submit,
      isSubmitting,
      isSubmitSuccessful,
      submitCount,
      formError,
      setFormError,
    }),
    [submit, isSubmitting, isSubmitSuccessful, submitCount, formError, setFormError],
  );
}
